import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { Definition, Product, Shape, SearchFormData, getMaterialTypes } from '@steelbuy/ts-shared';

export interface SearchFormDataContextValue {
    setSearchFormData: (searchFormData: SearchFormData) => void;
    getSearchFormData: () => SearchFormData;
}

const getDefaultSearchFormData: () => SearchFormData = () => ({
    materialType: getMaterialTypes()[0],
    shape: Shape.FLAT,
    product: Product.COIL,
    definition: Definition.HOT_ROLLED,
});

const defaultValue: SearchFormDataContextValue = {
    setSearchFormData: () => {
        // default noop
    },
    getSearchFormData: () => getDefaultSearchFormData(),
};

const SearchFormDataContext = createContext(defaultValue);

export const SearchFormDataContextProvider = ({
    children,
    defaultFormData,
}: {
    children: ReactNode;
    defaultFormData?: SearchFormData;
}) => {
    const [searchFormData, setSearchFormData] = useState<SearchFormData>(defaultFormData || getDefaultSearchFormData());

    const value = useMemo(
        () => ({
            getSearchFormData: () => searchFormData,
            setSearchFormData: (searchData: SearchFormData) => {
                if (searchData.product === Product.PACKAGE && searchFormData.product !== Product.PACKAGE) {
                    setSearchFormData({
                        ...searchData,
                        minWidth: undefined,
                        minLength: undefined,
                        maxWidth: undefined,
                        maxLength: undefined,
                        width: undefined,
                        length: undefined,
                        thickness: undefined,
                    });
                } else {
                    setSearchFormData(searchData);
                }
            },
        }),
        [searchFormData, setSearchFormData]
    );

    return <SearchFormDataContext.Provider value={value}>{children}</SearchFormDataContext.Provider>;
};

export const useSearchFormDataContext = () => useContext(SearchFormDataContext);
